import axios from "axios";

// HARDCODED - Always use production API (preview-only override for in-browser testing)
const BACKEND_URL = window.location.hostname.endsWith("preview.emergentagent.com")
  ? window.location.origin
  : "https://api.recoverymatch.com";

// IMPORTANT: use absolute baseURL, not "/api"
export const API_BASE = `${BACKEND_URL}/api`;

const getHeaders = () => {
  // Check for admin token first, then regular user token
  const adminToken = localStorage.getItem("recoverymatch_admin_token");
  const userToken = localStorage.getItem("token");
  const token = adminToken || userToken;
  
  // Debug logging for authentication issues
  if (!token) {
    console.warn('[API] No authentication token found in localStorage');
  }
  
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const api = axios.create({
  baseURL: API_BASE,
});

// Add auth headers to all requests
api.interceptors.request.use((config) => {
  const headers = getHeaders();
  if (headers.Authorization) {
    config.headers.Authorization = headers.Authorization;
  }
  return config;
});

// Add convenience methods that match the old API style
api.updateProfile = (data) => api.put('/profile', data);
api.uploadPhoto = (image) => api.post('/profile/photo', { image });
api.deletePhoto = (index) => api.delete(`/profile/photo/${index}`);

// Location
api.updateLocation = (latitude, longitude) => api.post('/profile/location', { latitude, longitude, location_enabled: true });
api.disableLocation = () => api.delete('/profile/location');

// Discovery
api.getDiscover = () => api.get('/discover');
api.swipe = (targetUserId, action) => api.post('/swipe', { target_user_id: targetUserId, action });
api.undoSwipe = () => api.post('/undo-swipe', {});
api.resetDeck = () => api.post('/swipe/reset-deck', {});

// Matches
api.getMatches = () => api.get('/matches');
api.getLikesReceived = () => api.get('/likes-received');
api.unmatch = (matchId) => api.post(`/unmatch/${matchId}`, {});

// Messages
api.getMessages = (matchId) => api.get(`/messages/${matchId}`);
api.sendMessage = (matchId, content) => api.post(`/messages/${matchId}`, { content });
api.markMessagesRead = (matchId) => api.put(`/messages/${matchId}/read`, {});

// Subscriptions
api.getSubscriptions = () => api.get('/subscriptions');
api.getMySubscription = () => api.get('/subscriptions/my');
api.upgradeSubscription = (tier) => api.post('/subscriptions/upgrade', { tier });
api.createCheckout = (lookup_key, origin_url) => api.post('/payments/checkout', { lookup_key, origin_url });
api.getPaymentStatus = (sessionId) => api.get(`/payments/status/${sessionId}`);
api.startTrial = () => api.post('/subscriptions/start-trial', {});
api.getTrialStatus = () => api.get('/subscriptions/trial-status');
api.boostProfile = () => api.post('/boost', {});

// RevenueCat subscriptions
api.getSubscriptionProducts = () => api.get('/revenuecat/products');
api.getSubscriptionStatus = (userId) => api.get(`/revenuecat/subscription/status/${userId}`);
api.syncSubscription = () => api.post('/revenuecat/subscription/sync', {});

// Recovery
api.getRecoveryPrograms = () => api.get('/recovery-programs');
api.getSobrietyMilestones = (userId) => api.get(`/sobriety-milestones/${userId}`);

// Reports
api.reportUser = (reportedUserId, reason, details) => api.post('/report', { reported_user_id: reportedUserId, reason, details });

// Leaderboard
api.getPopularLeaderboard = () => api.get('/leaderboard/popular');
api.getGenerousLeaderboard = () => api.get('/leaderboard/generous');

// Referral Program - Community Champion
api.getReferralCode = () => api.get('/referral/code');
api.getReferralStats = () => api.get('/referral/stats');
api.validateReferralCode = (code) => api.post('/referral/validate', { code });
api.getReferralLeaderboard = () => api.get('/referral/leaderboard');
api.getReferralImpact = () => api.get('/referral/impact');

// Profile Insights (Recovery+ feature)
api.getProfileViews = (days = 7) => api.get(`/insights/profile-views?days=${days}`);
api.getEngagementStats = () => api.get('/insights/engagement');
api.getWhoInterested = () => api.get('/insights/who-interested');
api.getSecondChance = () => api.get('/insights/second-chance');
api.reconsiderProfile = (matchId) => api.post(`/insights/second-chance/${matchId}`, {});
api.logProfileView = (userId) => api.post(`/insights/log-view/${userId}`, {});

// Block & Report
api.blockUser = (userId) => api.post('/block', { blocked_user_id: userId });
api.unblockUser = (userId) => api.delete(`/block/${userId}`);
api.getBlockedUsers = () => api.get('/blocked-users');

// Verification
api.submitVerification = (selfieUrl) => api.post('/verification/request', { selfie_url: selfieUrl });
api.getVerificationStatus = () => api.get('/verification/status');
api.cancelVerification = () => api.delete('/verification/cancel');

// Support Tickets
api.createTicket = (data) => api.post('/support/tickets', data);
api.getTickets = () => api.get('/support/tickets');
api.getTicket = (ticketId) => api.get(`/support/tickets/${ticketId}`);

// Coins & Gift Shop
api.getCoinBalance = () => api.get('/coins/balance');
api.getGiftCatalog = () => api.get('/gifts/catalog');
api.getCoinPackages = () => api.get('/coins/packages');
api.claimDailyLogin = () => api.post('/coins/daily-login', {});
api.getExpiringCoins = () => api.get('/coins/expiring');
api.replyToTicket = (ticketId, message) => api.post(`/support/tickets/${ticketId}/reply`, { message });
api.getUnreadTicketCount = () => api.get('/support/tickets/unread-count');

// Admin
api.getAdminStats = () => api.get('/admin/stats');
api.getAdminUsers = (params = {}) => api.get('/admin/users', { params });
api.searchUsers = (query) => api.get('/admin/users', { params: { search: query, limit: 10 } });
api.updateAdminUser = (userId, data) => api.put(`/admin/users/${userId}`, data);
api.deleteAdminUser = (userId) => api.delete(`/admin/users/${userId}`);
api.getAdminReports = (status) => api.get('/admin/reports', { params: { status } });
api.updateReport = (reportId, status) => api.put(`/admin/reports/${reportId}`, { status });
api.getAdminAnalytics = (days = 30) => api.get(`/admin/analytics?days=${days}`);
api.getAdminAnalyticsEvents = (days = 7, eventType = null) => {
  const params = { days };
  if (eventType) params.event_type = eventType;
  return api.get('/admin/analytics/events', { params });
};
api.getAdminAnalyticsFunnel = (days = 30) => api.get(`/admin/analytics/funnel?days=${days}`);
// Extended Analytics Endpoints
api.getAdminOnboarding = () => api.get('/admin/analytics/onboarding');
api.getAdminOnboardingDropoff = () => api.get('/admin/analytics/onboarding-dropoff');
api.getAdminMatching = () => api.get('/admin/analytics/matching');
api.getAdminMatchingTrends = (days = 30) => api.get(`/admin/analytics/matching/trends?days=${days}`);
api.getAdminUserJourney = () => api.get('/admin/analytics/user-journey');
api.getAdminOverview = () => api.get('/admin/analytics/overview');
api.getAdminGuidedMessaging = () => api.get('/admin/analytics/guided-messaging');
api.getAdminDailyEngagement = (days = 30) => api.get(`/admin/analytics/daily-engagement?days=${days}`);
api.getAdminSubscriptionFunnel = () => api.get('/admin/analytics/subscription-funnel');
api.getAdminRecoveryInsights = () => api.get('/admin/analytics/recovery-insights');
api.getAdminSuccessMetrics = () => api.get('/admin/analytics/success-metrics');
api.getAdminReferralProgram = () => api.get('/admin/analytics/referral-program');
api.getAdminSafetyModeration = () => api.get('/admin/analytics/safety-moderation');
api.getAdminActivityFeed = (limit = 50) => api.get(`/admin/analytics/activity-feed?limit=${limit}`);
api.getAdminAARRRMetrics = () => api.get('/admin/analytics/aarrr-metrics');
api.getAdminEconomy = () => api.get('/admin/economy');
api.adjustUserCoins = (userId, amount, reason) => api.post(`/admin/users/${userId}/adjust-coins`, { amount, reason });
api.getPhotosForReview = () => api.get('/admin/moderation/photos');
api.verifyUser = (userId) => api.post(`/admin/users/${userId}/verify`, {});
api.getVerificationRequests = (status = "pending") => api.get(`/admin/verification-requests?status=${status}`);
api.reviewVerification = (requestId, action, reason = "") => api.put(`/admin/verification-requests/${requestId}`, { action, reason });
api.bulkUserAction = (userIds, action) => api.post('/admin/users/bulk-action', { user_ids: userIds, action });
api.getSystemHealth = () => api.get('/admin/system');

// Admin Support Tickets
api.getAdminTickets = (status, priority, category) => {
  const params = {};
  if (status) params.status = status;
  if (priority) params.priority = priority;
  if (category) params.category = category;
  return api.get('/admin/support/tickets', { params });
};
api.getAdminTicket = (ticketId) => api.get(`/admin/support/tickets/${ticketId}`);
api.updateAdminTicket = (ticketId, data) => api.put(`/admin/support/tickets/${ticketId}`, data);
api.adminReplyToTicket = (ticketId, message) => api.post(`/admin/support/tickets/${ticketId}/reply`, { message });
api.getSupportStats = () => api.get('/admin/support/stats');

// Daily Check-in & Affirmations
api.getCheckinStatus = () => api.get('/checkin/status');
api.dailyCheckin = () => api.post('/checkin', {});
api.getCheckinHistory = () => api.get('/checkin/history');
api.getTodaysAffirmation = () => api.get('/affirmation/today');
api.getRandomAffirmation = () => api.get('/affirmation/random');
api.saveAffirmation = (affirmation) => api.post('/affirmation/save', { affirmation });
api.unsaveAffirmation = (affirmation) => api.delete('/affirmation/unsave', { data: { affirmation } });
api.getSavedAffirmations = () => api.get('/affirmations/saved');

// Onboarding (Hybrid Model)
api.getOnboardingStatus = () => api.get('/onboarding/status');
api.submitRecoveryBasics = (data) => api.post('/onboarding/recovery-basics', data);
api.submitRecoveryImportance = (data) => api.post('/onboarding/recovery-importance', data);
api.submitSpirituality = (data) => api.post('/onboarding/spirituality', data);
api.submitSponsorSupport = (data) => api.post('/onboarding/sponsor-support', data);
api.submitCoreValues = (data) => api.post('/onboarding/core-values', data);
api.submitRelationshipIntent = (data) => api.post('/onboarding/relationship-intent', data);
api.submitBoundariesPacing = (data) => api.post('/onboarding/boundaries-pacing', data);
api.submitLifestyle = (data) => api.post('/onboarding/lifestyle', data);
api.submitSubstances = (data) => api.post('/onboarding/substances', data);
api.submitPreferences = (data) => api.post('/onboarding/preferences', data);
api.getAvailablePrompts = () => api.get('/onboarding/prompts');
api.submitProfilePrompts = (data) => api.post('/onboarding/profile-prompts', data);
api.submitPhotos = (data) => api.post('/onboarding/photos', data);
api.completeOnboarding = () => api.post('/onboarding/complete', {});
api.getCompatibilityPreview = () => api.get('/onboarding/compatibility-preview');

// Daily Matches (Hybrid Model)
api.getDailyMatches = () => api.get('/matches/daily');
api.getMatchDetail = (matchedUserId) => api.get(`/matches/${matchedUserId}`);
api.expressInterest = (matchedUserId) => api.post(`/matches/${matchedUserId}/interested`, {});
api.passOnMatch = (matchedUserId) => api.post(`/matches/${matchedUserId}/not-aligned`, {});
api.getMutualMatches = () => api.get('/matches/mutual');
api.getMatchStats = () => api.get('/matches/stats');

// Celebration endpoints
api.getUncelebratedMatches = () => api.get('/matches/uncelebrated');
api.markMatchCelebrated = (matchedUserId) => api.post(`/matches/${matchedUserId}/mark-celebrated`, {});

// See Who Liked You (Recovery+ Feature)
api.getWhoLikedMe = () => api.get('/matches/liked-me');
api.likeBack = (likerUserId) => api.post(`/matches/like-back/${likerUserId}`, {});

// Super Like (Recovery Elite Feature)
api.getSuperLikesStatus = () => api.get('/matches/super-likes/status');
api.sendSuperLike = (matchedUserId) => api.post(`/matches/${matchedUserId}/super-like`, {});

// Advanced Filters (Recovery Elite Feature)
api.getFilterOptions = () => api.get('/advanced/filters/options');
api.getCurrentFilters = () => api.get('/advanced/filters/current');
api.updateAdvancedFilters = (filters) => api.put('/advanced/filters/update', filters);
api.clearAdvancedFilters = () => api.delete('/advanced/filters/clear');
api.getPriorityStatus = () => api.get('/advanced/priority-status');

// Guided Messaging (Phase 3)
api.getConversations = () => api.get('/guided-messaging/conversations');
api.getGuidedPrompts = () => api.get('/guided-messaging/prompts');
api.getGuidedChatStatus = (otherUserId) => api.get(`/guided-messaging/status/${otherUserId}`);
api.sendGuidedMessage = (otherUserId, data) => api.post(`/guided-messaging/send/${otherUserId}`, data);
api.sendFreeformMessage = (otherUserId, data) => api.post(`/guided-messaging/freeform/${otherUserId}`, data);
api.getGuidedMessages = (otherUserId) => api.get(`/guided-messaging/messages/${otherUserId}`);
api.getUnreadCount = () => api.get('/guided-messaging/unread-count');

// Read Receipts (Recovery+ Feature)
api.getReadReceipts = (otherUserId) => api.get(`/guided-messaging/read-receipts/${otherUserId}`);
api.markMessagesAsRead = (otherUserId) => api.post(`/guided-messaging/mark-read/${otherUserId}`, {});
api.getTypingStatus = (otherUserId) => api.get(`/guided-messaging/typing/${otherUserId}`);
api.setTypingStatus = (otherUserId) => api.post(`/guided-messaging/typing/${otherUserId}`, {});

// Message Reactions
api.getReactions = (conversationId) => api.get(`/guided-messaging/reactions/${conversationId}`);
api.addReaction = (messageId, data) => api.post(`/guided-messaging/reactions/${messageId}`, data);
api.removeReaction = (messageId) => api.delete(`/guided-messaging/reactions/${messageId}`);

// Admin - Messaging Analytics
api.getAdminMessagingStats = () => api.get('/admin/messaging/stats');
api.getAdminMessagingTrends = (days = 14) => api.get(`/admin/messaging/trends?days=${days}`);
api.getAdminPromptStats = () => api.get('/admin/messaging/prompt-stats');
api.getAdminMessagingEngagement = () => api.get('/admin/messaging/engagement');
api.getAdminRecentConversations = (limit = 10) => api.get(`/admin/messaging/recent-conversations?limit=${limit}`);

// Admin - Analytics (Hybrid Model)
api.getAdminOnboardingAnalytics = () => api.get('/admin/analytics/onboarding');
api.getAdminMatchingAnalytics = () => api.get('/admin/analytics/matching');

// New Admin Analytics
api.getAdminSponsorSupport = () => api.get('/admin/analytics/sponsor-support');
api.getAdminFiltersUsage = () => api.get('/admin/analytics/filters-usage');
